import React from 'react';
import { Box, Button, Chip, Skeleton, Typography } from '@mui/material';
import { ArrowForward as ArrowIcon, CheckCircle as CheckIcon, EditCalendar as PlanIcon, Schedule as ClockIcon } from '@mui/icons-material';
import { formatHours } from '../monthly/format';
import { parseLocalDate } from '../../utils/dateHelpers';
import { DAY_COLORS, GREEN, GREEN_DARK, GREEN_SOFT, INK, LINE, MUTED, useThemeColors } from './homeTheme';

const MAX_CHIPS = 6;

function ProgressRing({ value, color, label }) {
  const colors = useThemeColors();
  const size = 116;
  const stroke = 11;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(100, value));
  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${Math.round(pct)}% ${label}`}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={colors['ring-track']} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${(c * pct) / 100} ${c}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dasharray 0.6s ease' }}
        />
      </svg>
      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: INK, lineHeight: 1 }}>{Math.round(pct)}%</Typography>
        <Typography sx={{ fontSize: 11, color: MUTED, mt: 0.5, textAlign: 'center', lineHeight: 1.2, px: 2 }}>{label}</Typography>
      </Box>
    </Box>
  );
}

/** Riquadro principale: avanzamento del mese e la prossima cosa da fare. */
export default function HomeHero({ stats, monthName, period, onOpenDay, onOpenMonthly }) {
  if (!stats.ready) {
    return <Skeleton variant="rounded" height={156} sx={{ borderRadius: 4 }} />;
  }

  const shortDay = (d) => parseLocalDate(d.dateKey).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric' });
  let ring;
  let icon;
  let title;
  let text;
  let action;
  let chips = null;

  if (period === 'future') {
    ring = { value: stats.monthTarget ? (stats.forecastAhead / stats.monthTarget) * 100 : 0, color: DAY_COLORS.future, label: 'planned' };
    icon = <PlanIcon sx={{ color: '#3B82F6' }} />;
    title = `Planning ${monthName}`;
    text = `${formatHours(stats.forecastAhead)}h forecast out of ${formatHours(stats.monthTarget)}h of working time.`;
    action = { label: 'Plan in Monthly View', onClick: onOpenMonthly };
  } else if (stats.incompleteDays.length) {
    const n = stats.incompleteDays.length;
    ring = { value: stats.expectedToDate ? (stats.trackedToDate / stats.expectedToDate) * 100 : 0, color: DAY_COLORS.under, label: 'of expected hours' };
    icon = <ClockIcon sx={{ color: DAY_COLORS.under }} />;
    title = `${n} working day${n === 1 ? '' : 's'} still need${n === 1 ? 's' : ''} hours`;
    text = `${formatHours(stats.missing)}h missing ${period === 'current' ? 'up to yesterday' : `in ${monthName}`}. Pick a day to fill it in.`;
    chips = stats.incompleteDays;
    action = { label: `Fill in ${shortDay(stats.incompleteDays[0])}`, onClick: () => onOpenDay(stats.incompleteDays[0].dateKey) };
  } else if (stats.todayPending) {
    ring = { value: 100, color: GREEN, label: 'of expected hours' };
    icon = <ClockIcon sx={{ color: GREEN }} />;
    title = 'Everything is up to date';
    text = 'Previous days are complete. Today isn’t tracked yet.';
    action = { label: 'Track today', onClick: () => onOpenDay(stats.todayPending.dateKey) };
  } else {
    ring = { value: 100, color: GREEN, label: 'of expected hours' };
    icon = <CheckIcon sx={{ color: GREEN }} />;
    title = 'All caught up';
    text = period === 'current' ? 'Every working day so far has its hours. Nice work.' : `${monthName} is complete.`;
    action = { label: 'Open Monthly View', onClick: onOpenMonthly, subtle: true };
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 2, md: 3 },
        flexDirection: { xs: 'column', sm: 'row' },
        p: { xs: 2, md: 3 },
        borderRadius: 4,
        border: `1px solid ${LINE}`,
        bgcolor: GREEN_SOFT,
      }}
    >
      <ProgressRing {...ring} />
      <Box sx={{ flex: 1, minWidth: 0, textAlign: { xs: 'center', sm: 'left' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
          {icon}
          <Typography sx={{ fontSize: { xs: 19, md: 22 }, fontWeight: 800, color: INK, lineHeight: 1.2 }}>{title}</Typography>
        </Box>
        <Typography sx={{ fontSize: 14, color: MUTED, mt: 0.75 }}>{text}</Typography>
        {chips && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
            {chips.slice(0, MAX_CHIPS).map((d) => (
              <Chip
                key={d.dateKey}
                size="small"
                label={`${shortDay(d)} · ${d.actual ? `${formatHours(d.actual)}h` : '0h'}`}
                onClick={() => onOpenDay(d.dateKey)}
                sx={{
                  bgcolor: 'var(--rt-paper)',
                  border: `1px solid ${d.actual ? DAY_COLORS.under : DAY_COLORS.missing}`,
                  color: d.actual ? 'var(--rt-warn)' : 'var(--rt-danger-strong)',
                  fontWeight: 600,
                }}
              />
            ))}
            {chips.length > MAX_CHIPS && <Chip size="small" label={`+${chips.length - MAX_CHIPS} more`} onClick={onOpenMonthly} sx={{ bgcolor: 'var(--rt-paper)' }} />}
          </Box>
        )}
      </Box>
      <Button
        variant={action.subtle ? 'outlined' : 'contained'}
        color="success"
        endIcon={<ArrowIcon />}
        onClick={action.onClick}
        sx={{
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 2.5,
          px: 2.5,
          py: 1,
          flexShrink: 0,
          boxShadow: 'none',
          ...(action.subtle ? { borderColor: GREEN, color: GREEN_DARK, bgcolor: 'var(--rt-paper)' } : { bgcolor: GREEN, '&:hover': { bgcolor: GREEN_DARK, boxShadow: 'none' } }),
        }}
      >
        {action.label}
      </Button>
    </Box>
  );
}
